/**
 * Plays a session recording from encrypted HLS. There is no video file to
 * save: segments are AES-128 encrypted and the key is handed only to a
 * logged-in member of the batch. On top of that the player hides download,
 * picture-in-picture and casting, blocks the context menu, and shows
 * "Flowstate.net.in property" as a moving watermark (kept in fullscreen, put
 * back if removed) so a screen recording carries whose content it is.
 *
 * It also resumes where the viewer left off and reports what they watched.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Box, Button, IconButton, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

import { accessToken, apiOrigin, getPlaybackToken, playlistUrl, sendProgress } from "./sessionsApi";

const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const BEAT_MS = 15000;
const FONT = "Be Vietnam Pro";
export const WATERMARK = "Flowstate.net.in property";

export const formatClock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
};

/** Joins played time into spans; a jump (seek) starts a new span. */
export class SpanTracker {
  constructor() {
    this.spans = [];
    this.current = null;
    this.last = null;
  }

  tick(time, rate = 1) {
    if (this.last !== null && time >= this.last && time - this.last <= 1.5 * Math.max(1, rate)) {
      if (!this.current) this.current = [this.last, time];
      else this.current[1] = time;
    } else if (this.current) {
      this.spans.push(this.current);
      this.current = null;
    }
    this.last = time;
  }

  seeked(time) {
    if (this.current) this.spans.push(this.current);
    this.current = null;
    this.last = time;
  }

  drain() {
    const out = [...this.spans, ...(this.current ? [this.current] : [])].filter(([a, b]) => b - a > 0.2);
    this.spans = [];
    if (this.current) this.current = [this.current[1], this.current[1]];
    return out.map(([a, b]) => [Math.round(a * 10) / 10, Math.round(b * 10) / 10]);
  }
}

/**
 * `theatre`: the player fills the box it is given (the session page's video
 * column, full height) instead of keeping 16:9 under the header; `onTheatre`
 * toggles that and shows its button. Real full screen is unchanged.
 */
export default function SecurePlayer({ recording, onProgress, controlRef, startAt, onStarted, theatre, onTheatre }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const tracker = useRef(new SpanTracker());
  const resumed = useRef(false);
  const [error, setError] = useState("");
  const [speed, setSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [resumeNote, setResumeNote] = useState("");
  const [reload, setReload] = useState(0);

  // The brand, not the viewer: a screen recording shows whose property it is.
  const label = WATERMARK;

  // ─── Loading ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    setError("");
    resumed.current = false;
    let hls;
    let cancelled = false;
    const url = playlistUrl(recording.id);

    if (Hls.isSupported()) {
      hls = new Hls({
        maxBufferLength: 30,
        // Our API needs the login; cloud-storage segment URLs are pre-signed and must not get it.
        xhrSetup: (xhr, target) => {
          if (target.startsWith(apiOrigin)) xhr.setRequestHeader("Authorization", `Bearer ${accessToken()}`);
        },
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        const code = data.response?.code;
        if (code === 401 || code === 403) {
          if (data.details === "fragLoadError") { // signed segment links expired: fetch a fresh playlist
            const at = video.currentTime;
            hls.loadSource(url);
            hls.once(Hls.Events.MANIFEST_PARSED, () => { video.currentTime = at; video.play().catch(() => {}); });
            return;
          }
          setError("You don't have access to this recording, or your session expired. Reload the page.");
        } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setError("This recording couldn't be played. Try reloading the page.");
        }
      });
      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Older iPhones: the native player can't send headers, so use a short-lived token.
      getPlaybackToken(recording.id)
        .then((token) => { if (!cancelled) video.src = `${url}?t=${encodeURIComponent(token)}`; })
        .catch(() => setError("Couldn't start playback. Reload the page."));
    } else {
      setError("This browser can't play protected video. Use a recent Chrome, Edge, Firefox or Safari.");
    }
    return () => {
      cancelled = true;
      if (hls) hls.destroy();
      video.removeAttribute("src");
      video.load();
    };
  }, [recording.id, reload]);

  // ─── Resume, speed ────────────────────────────────────────────────────────
  const onLoadedMetadata = () => {
    const video = videoRef.current;
    const at = recording.my_position || 0;
    if (!resumed.current && startAt !== undefined && startAt !== null && startAt >= 0) {
      // Opened from a tutor answer's "Class 2 · 1:12:30": go there, not to where they left off.
      video.currentTime = Math.min(startAt, Math.max(0, (video.duration || startAt + 1) - 1));
      setResumeNote(`From ${formatClock(startAt)}`);
      if (onStarted) onStarted();
    } else if (!resumed.current && at > 10 && at < (video.duration || recording.duration_seconds) - 10) {
      video.currentTime = at;
      setResumeNote(`Resumed at ${formatClock(at)}`);
    }
    resumed.current = true;
    video.playbackRate = speed;
    tracker.current.seeked(video.currentTime);
  };

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  // What the AI tutor needs from the player: where the student is, and a way
  // to jump to the moment an answer cites.
  useEffect(() => {
    if (!controlRef) return undefined;
    controlRef.current = {
      recordingId: recording.id,
      time: () => videoRef.current?.currentTime || 0,
      seek: (t) => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = Math.max(0, t);
        tracker.current.seeked(video.currentTime);
        video.play().catch(() => {});
      },
    };
    return () => { if (controlRef.current?.recordingId === recording.id) controlRef.current = null; };
  }, [controlRef, recording.id]);

  // ─── Progress ─────────────────────────────────────────────────────────────
  const flush = useCallback((keepalive = false) => {
    const video = videoRef.current;
    if (!video) return;
    const spans = tracker.current.drain();
    if (!spans.length && !keepalive) return;
    sendProgress(recording.id, video.currentTime, spans, keepalive).then((r) => {
      if (r && r.ok && onProgress) r.json().then((d) => onProgress(recording.id, d)).catch(() => {});
    });
  }, [recording.id, onProgress]);

  useEffect(() => {
    const timer = setInterval(() => flush(false), BEAT_MS);
    const onHide = () => { if (document.visibilityState === "hidden") flush(true); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      flush(true);
    };
  }, [flush]);

  // ─── Watermark ────────────────────────────────────────────────────────────
  // Built outside React so removing it from dev tools can't break the player:
  // a missing, hidden or faded watermark pauses playback and is put back.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let node = null;
    let timer;
    const style = (el) => Object.assign(el.style, {
      position: "absolute", pointerEvents: "none", zIndex: "2", whiteSpace: "nowrap",
      color: "rgba(255,255,255,0.42)", textShadow: "0 0 2px rgba(0,0,0,0.6)", fontFamily: FONT,
      fontSize: "13px", fontWeight: "600", transition: "top 1.2s ease, left 1.2s ease",
      display: "block", visibility: "visible", opacity: "1",
    });
    const place = () => {
      if (!node) return;
      node.style.top = `${6 + Math.random() * 78}%`;
      node.style.left = `${4 + Math.random() * 62}%`;
    };
    const create = () => {
      node = document.createElement("div");
      node.setAttribute("data-testid", "watermark");
      node.setAttribute("aria-hidden", "true");
      node.textContent = label;
      style(node);
      place();
      wrap.appendChild(node);
    };
    const tampered = () => {
      if (!node || !wrap.contains(node) || node.textContent !== label) return true;
      const cs = window.getComputedStyle(node);
      return cs.display === "none" || cs.visibility === "hidden" || parseFloat(cs.opacity || "1") < 0.9;
    };
    create();
    const move = () => { place(); timer = setTimeout(move, 8000 + Math.random() * 7000); };
    timer = setTimeout(move, 8000);
    const observer = new MutationObserver(() => {
      if (!tampered()) return;
      videoRef.current?.pause();
      observer.disconnect();
      if (node && node.parentNode) node.parentNode.removeChild(node);
      create();
      observer.observe(wrap, { childList: true, subtree: true, attributes: true, characterData: true });
    });
    observer.observe(wrap, { childList: true, subtree: true, attributes: true, characterData: true });
    return () => {
      observer.disconnect();
      clearTimeout(timer);
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
  }, [label]);

  // ─── Fullscreen (the wrapper, so the watermark stays on top) ──────────────
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === wrapRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapRef.current?.requestFullscreen?.().catch(() => {});
  };

  const block = (event) => event.preventDefault();

  const filling = theatre && !fullscreen;

  return (
    <Box sx={theatre ? { height: "100%", display: "flex", flexDirection: "column" } : undefined}>
      <Box
        ref={wrapRef}
        onContextMenu={block}
        onDragStart={block}
        sx={{ position: "relative", backgroundColor: "#000", borderRadius: fullscreen || filling ? 0 : "10px", overflow: "hidden",
          aspectRatio: fullscreen || filling ? "auto" : "16 / 9", height: fullscreen ? "100%" : "auto", userSelect: "none",
          // A wide column must not push the controls off the screen.
          // The header and title above it take ~260px: leave room below for the speed row too.
          maxHeight: fullscreen || filling ? "none" : "max(240px, calc(100vh - 360px))", mx: "auto",
          ...(filling ? { flex: 1, minHeight: 0, width: "100%" } : {}) }}
      >
        <video
          ref={videoRef}
          controls
          playsInline
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          disablePictureInPicture
          disableRemotePlayback
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={(e) => tracker.current.tick(e.currentTarget.currentTime, e.currentTarget.playbackRate)}
          onSeeking={(e) => tracker.current.seeked(e.currentTarget.currentTime)}
          onPause={() => flush(false)}
          onEnded={() => flush(false)}
          onContextMenu={block}
          style={{ width: "100%", height: "100%", display: "block", backgroundColor: "#000" }}
          aria-label={`Recording, part ${recording.part}`}
        />
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 3, display: "flex", gap: 0.75 }}>
          {onTheatre && !fullscreen && (
            <Tooltip title={theatre ? "Back to the page (Esc)" : "Fill the screen, keep Ask AI open"}>
              <IconButton onClick={onTheatre} size="small" aria-label={theatre ? "Exit theatre mode" : "Theatre mode"}
                sx={{ color: "#fff", backgroundColor: "rgba(0,0,0,0.35)", "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" } }}>
                {theatre ? <CloseFullscreenIcon fontSize="small" /> : <FitScreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={fullscreen ? "Exit full screen" : "Full screen"}>
            <IconButton onClick={toggleFullscreen} size="small" aria-label="Full screen"
              sx={{ color: "#fff", backgroundColor: "rgba(0,0,0,0.35)", "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" } }}>
              {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        {error && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 1, color: "#fff", backgroundColor: "rgba(0,0,0,0.75)", p: 2, textAlign: "center", zIndex: 4 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 14 }}>{error}</Typography>
            <Button size="small" variant="outlined" sx={{ color: "#fff", borderColor: "#fff" }} onClick={() => setReload((n) => n + 1)}>
              Try again
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: theatre ? 0.75 : 1, flexWrap: "wrap", flexShrink: 0 }}>
        <Typography sx={{ fontSize: 13, color: theatre ? "#cbd5e1" : "#6b7280", fontFamily: FONT }}>Speed</Typography>
        <Select size="small" value={speed} onChange={(e) => setSpeed(Number(e.target.value))}
          sx={{ fontSize: 13, height: 30, ...(theatre ? { color: "#e5e7eb", "& fieldset": { borderColor: "#4b5563" }, "& svg": { color: "#e5e7eb" } } : {}) }}
          inputProps={{ "aria-label": "Playback speed" }}>
          {SPEEDS.map((s) => <MenuItem key={s} value={s}>{s}×</MenuItem>)}
        </Select>
        {resumeNote && (
          <Typography sx={{ fontSize: 13, color: theatre ? "#cbd5e1" : "#374151", fontFamily: FONT, ml: 1 }}>
            {resumeNote} ·{" "}
            <Button size="small" sx={{ textTransform: "none", p: 0, minWidth: 0, verticalAlign: "baseline" }}
              onClick={() => { videoRef.current.currentTime = 0; setResumeNote(""); }}>
              Start over
            </Button>
          </Typography>
        )}
      </Box>
    </Box>
  );
}
